import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSeason } from "@/lib/SeasonContext";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import TrendIcon from "@/components/app/TrendIcon";
import { Trophy, CalendarDays, Target, ShieldAlert } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { activeSeason } = useSeason();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, [activeSeason]);

  if (!data) return <div className="p-8 text-ink-300">Loading dashboard…</div>;

  const { record, team_ppg, opp_ppg, team_fg_pct, ppg_chart, top_performers, next_game } = data;

  return (
    <div className="p-6 md:p-10 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="eyebrow" data-testid="dashboard-season-label">{activeSeason ? `${activeSeason} Season` : "Season"}</p>
          <h1 className="heading-1">Coach <span className="text-burgundy">Command</span></h1>
        </div>
        <div className="text-right">
          <p className="eyebrow">Record</p>
          <p className="font-display text-4xl md:text-5xl font-black" data-testid="record-display">
            {record.wins}<span className="text-ink-400">-</span>{record.losses}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KpiCard icon={Target} label="Team PPG" value={team_ppg} tone="burgundy" testId="kpi-team-ppg" />
        <KpiCard icon={ShieldAlert} label="Opp PPG" value={opp_ppg} tone="ink" testId="kpi-opp-ppg" />
        <KpiCard icon={Trophy} label="Team FG%" value={`${team_fg_pct}%`} tone="ink" testId="kpi-fg-pct" />
        <KpiCard icon={CalendarDays} label="Games Played" value={record.games_played} tone="ink" testId="kpi-gp" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 bg-ink-800 rounded-lg border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="heading-3">Points · Last 6 Games</h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1.5 bg-burgundy" /> NPHS</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1.5 bg-ink-500" /> Opp</span>
            </div>
          </div>
          {ppg_chart?.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ppg_chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="#71717A" tick={{ fill: "#A1A1AA", fontSize: 11 }} interval={0} />
                  <YAxis stroke="#71717A" tick={{ fill: "#A1A1AA", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#1C1C21", border: "1px solid #27272A", color: "#fff" }} />
                  <Line type="monotone" dataKey="nphs" stroke="#6B0F1A" strokeWidth={3} dot={{ fill: "#fff", r: 4 }} />
                  <Line type="monotone" dataKey="opp" stroke="#71717A" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "#71717A", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-ink-400 py-12 text-center">No games played yet. Save stats to start the chart.</p>
          )}
        </section>

        <section className="bg-ink-800 rounded-lg border border-white/5 p-6">
          <h3 className="heading-3 mb-4">Next Game</h3>
          {next_game ? (
            <div data-testid="next-game-card">
              <p className="eyebrow">{next_game.home_away === "home" ? "Home" : "Away"}</p>
              <p className="font-display text-2xl font-bold mt-1">vs {next_game.opponent}</p>
              <p className="text-ink-300 text-sm mt-2">{next_game.date}{next_game.location ? ` · ${next_game.location}` : ""}</p>
              {next_game.playoff && <span className="inline-block mt-3 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded uppercase tracking-wider">Playoff</span>}
            </div>
          ) : (
            <p className="text-ink-400">No upcoming games scheduled.</p>
          )}
          <Link to="/schedule" className="block mt-6 text-xs uppercase tracking-wider text-burgundy hover:text-burgundy-hover">
            View full schedule →
          </Link>
        </section>
      </div>

      <section className="mt-8 bg-ink-800 rounded-lg border border-white/5 p-6">
        <h3 className="heading-3 mb-4">Top Performers</h3>
        {top_performers?.length ? (
          <div className="space-y-3">
            {top_performers.map((tp) => (
              <Link
                key={tp.player.id}
                to={`/player/${tp.player.slug}`}
                className="flex items-center justify-between gap-4 p-3 -mx-3 rounded-md hover:bg-ink-900 transition"
                data-testid={`top-performer-${tp.player.slug}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <PlayerAvatar player={tp.player} size="md" />
                  <div className="min-w-0">
                    <p className="font-display font-bold uppercase truncate">{tp.player.player_name}</p>
                    <p className="text-xs text-ink-400">#{tp.player.jersey_number || "—"} · {tp.player.position || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <Stat label="PPG" value={tp.ppg} trendDir={tp.trend} />
                  <Stat label="REB" value={tp.reb_avg} />
                  <Stat label="AST" value={tp.ast_avg} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-ink-400">No stats recorded yet.</p>
        )}
      </section>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone, testId }) {
  return (
    <div
      className={`rounded-lg p-5 border ${tone === "burgundy" ? "bg-burgundy/15 border-burgundy/40" : "bg-ink-800 border-white/5"}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">{label}</p>
        <Icon className={`w-5 h-5 ${tone === "burgundy" ? "text-burgundy" : "text-ink-400"}`} strokeWidth={2.4} />
      </div>
      <p className="font-display text-3xl md:text-4xl font-black">{value}</p>
    </div>
  );
}

function Stat({ label, value, trendDir }) {
  return (
    <div className="hidden sm:block">
      <p className="eyebrow text-[9px]">{label}</p>
      <div className="flex items-center gap-1 justify-end">
        <span className="font-display font-bold text-lg">{value}</span>
        {trendDir && <TrendIcon direction={trendDir} className="w-3.5 h-3.5" />}
      </div>
    </div>
  );
}
